// @file Settings Vuex Store.
import backendState from '@@/bits/backend_state'
import {
  isPaidLibraryBilling,
  normalizedOppositePaymentSchedule,
  normalizedPaymentSchedule,
  SwitchType,
} from '@@/bits/billing_helper'
import { ALERT_ICON, THINKING_FACE_ICON } from '@@/bits/confirmation_dialog'
import currentTenant from '@@/bits/current_tenant'
import currentUser from '@@/bits/current_user'
import device from '@@/bits/device'
import { captureFetchException, setScope } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { translatePrice, __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { buildUrlFromPath, navigateTo, reload, transformCurrentUrl } from '@@/bits/location'
import { poll } from '@@/bits/polling'
import * as PadletApi from '@@/dashboard/padlet_api'
import {
  CancellationType,
  ContentSafetyEntity,
  LibraryMembershipTier,
  LibrarySubscriptionStatus,
  LibraryType,
} from '@@/enums'
import stopHandIcon from '@@/images/stop_hand.svg'
import { useContentSafetySettingsStore } from '@@/pinia/content_safety_settings'
import { useLibraryPlansStore } from '@@/pinia/library_plans'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSettingsBackpackPermissionsStore } from '@@/pinia/settings_backpack_permissions'
import { useSettingsBriefcasePermissionsStore } from '@@/pinia/settings_briefcase_permissions'
import { useSettingsDevelopersStore } from '@@/pinia/settings_developers'
import { useSettingsIntegrationPermissionsStore } from '@@/pinia/settings_integration_permissions'
import { useSettingsLibraryInfoStore } from '@@/pinia/settings_library_info'
import { useSettingsLibraryMembersStore } from '@@/pinia/settings_library_members'
import { useSettingsLibrarySecurityStore } from '@@/pinia/settings_library_security'
import { SettingView, useSettingsNavigationStore } from '@@/pinia/settings_navigation'
import { useSettingsNotificationsStore } from '@@/pinia/settings_notifications'
import { useSettingsSchoolPermissionsStore } from '@@/pinia/settings_school_permissions'
import { useSettingsTenantAnalyticsStore } from '@@/pinia/settings_tenant_analytics'
import { useSettingsTenantInfoStore } from '@@/pinia/settings_tenant_info'
import { useSettingsUserDashboardStore } from '@@/pinia/settings_user_dashboard'
import { useSettingsUserInfoStore } from '@@/pinia/settings_user_info'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { fetchJson } from '@@/surface/api_fetch'
import type {
  ChangeCardUrl,
  EstimateCancellationRefundResult,
  EstimateSwitchingCostResult,
  EstimateTeamNeonDowngradeRefund,
  GoogleAppLicensingValidateResponse,
  Library,
  LibraryBilling,
  LibraryDowngradeToTeamNeonResult,
  LibraryId,
  LibraryInvoice,
  LibraryUserQuotaInfo,
  Plan,
  Tenant,
  UndoLibraryScheduledChangeResult,
  UndoScheduledChangeResult,
  UserOptions,
  Wall,
} from '@@/types'
import basicInfo from '@@/vuexstore/modules/basic_info'
import browser from '@@/vuexstore/modules/browser'
import contentPicker from '@@/vuexstore/modules/content_picker'
import alertDialog from '@@/vuexstore/modules/global_alert_dialog'
import confirmationDialog, {
  ConfirmationDialogLayoutVariant,
  OzConfirmationDialogBoxButtonScheme,
} from '@@/vuexstore/modules/global_confirmation_dialog'
import inputDialog from '@@/vuexstore/modules/global_input_dialog'
import globalSnackbar from '@@/vuexstore/modules/global_snackbar'
import libraryPlans from '@@/vuexstore/modules/library_plans'
import plans from '@@/vuexstore/modules/plans'
import spinnerModal from '@@/vuexstore/modules/spinner_modal'
import logger, { shouldLog } from '@@/vuexstore/plugins/logger'
import type { Billing, Invoice, JsonAPIResource, JsonAPIResponse, User } from '@padlet/arvo'
import { configure as configureArvo } from '@padlet/arvo'
import type { FetchError } from '@padlet/fetch'
import { HTTPMethod } from '@padlet/fetch'
import { merge, sortBy } from 'lodash-es'
import { storeToRefs } from 'pinia'
import type { Plugin, StoreOptions } from 'vuex'

enum ScheduledChange {
  SwitchToAnnual = 'SwitchToAnnual',
  SwitchToMonthly = 'SwitchToMonthly',
  DowngradeToNeon = 'DowngradeToNeon',
  DowngradeToGold = 'DowngradeToGold',
}

enum LibraryScheduledChange {
  SwitchToAnnual = 'SwitchToAnnual',
  SwitchToMonthly = 'SwitchToMonthly',
  DowngradeToTeamNeon = 'DowngradeToTeamNeon',
}

enum GenericStep {
  AboveQuota = 'AboveQuota',
  ChooseDate = 'ChooseDate',
  Now = 'Now',
  NowDifferentCurrency = 'NowDifferentCurrency',
  ConfirmNow = 'ConfirmNow',
  Later = 'Later',
  ConfirmLater = 'ConfirmLater',
  ChooseDateGold = 'ChooseDateGold',
  NowGold = 'NowGold',
  NowGoldDifferentCurrency = 'NowGoldDifferentCurrency',
  LaterGold = 'LaterGold',
  ConfirmNowGold = 'ConfirmNowGold',
  ConfirmNowGoldDifferentCurrency = 'ConfirmNowGoldDifferentCurrency',
  ConfirmLaterGold = 'ConfirmLaterGold',
}

type DowngradeStep = GenericStep

type CrossgradeStep = Exclude<GenericStep, typeof GenericStep.AboveQuota>

enum LibraryGenericStep {
  ChooseDate = 'ChooseDate',
  Now = 'Now',
  ConfirmNow = 'ConfirmNow',
  Later = 'Later',
  ConfirmLater = 'ConfirmLater',
}

type LibraryDowngradeStep = LibraryGenericStep

interface CommunicationSetting {
  wallCreate: boolean
  productUpdate: boolean
}

const unpaidInvoiceErrorMessage = __(
  'Sorry, we are not able to process your request because your last invoice is unpaid. Please update your payment method and try again.',
)

const unableToProcessErrorMessage = __(`We're sorry, we were unable to process your request. Please try again later.`)
interface DashboardSettingsState {
  // init state
  user: User
  userOptions: UserOptions
  tenant: Tenant
  walls: Wall[]
  communicationSettings: CommunicationSetting
  wallNotifications: Record<string, boolean>
  chatWidgetUserToken: string
  // billing
  isApp: boolean
  isElectronApp: boolean
  billing: Billing | null
  invoices: Invoice[]
  isProcessingDowngrade: boolean
  downgradeError: string
  isProcessingCrossgrade: boolean
  crossgradeError: string
  isLoadingCancellationRefundInformation: boolean
  xDowngradeModal: boolean
  downgradeStep: DowngradeStep | null
  xCrossgradeModal: boolean
  crossgradeStep: CrossgradeStep | null
  isLoadingCrossgradeInformation: boolean
  isLoadingGoldDowngradeInformation: boolean
  estimatedCrossgradeCost: EstimateSwitchingCostResult | null
  estimatedGoldDowngradeCost: EstimateSwitchingCostResult | null
  isChrome: boolean
  isFirefox: boolean
  isSafari: boolean
  isTouchable: boolean
  isMac: boolean
  fetchedCommunicationInformation: boolean
  isOnline: boolean
  // Library
  libraries: Library[]
  // Library billing
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isLoadingLibraryBillingInformation: boolean
  libraryBilling: LibraryBilling | null
  libraryBillingError: string
  isLoadingLibraryInvoices: boolean
  libraryInvoices: LibraryInvoice[]
  libraryInvoicesError: string
  xLibraryDowngradeModal: boolean
  xClassroomLibraryDowngradeModal: boolean
  libraryDowngradeStep: LibraryDowngradeStep | null
  libraryDowngradeError: string
  isProcessingLibraryDowngrade: boolean
  isLoadingTeamNeonDowngradeInformation: boolean
  isLoadingLibraryChangeCardUrl: boolean
  estimatedTeamNeonDowngradeRefund: EstimateTeamNeonDowngradeRefund | null
  estimatedCancellationRefund: EstimateCancellationRefundResult | null
}

const plugins: Array<Plugin<DashboardSettingsState>> = []
if (shouldLog) plugins.push(logger)

export const storeOptions: StoreOptions<DashboardSettingsState> = {
  plugins,
  modules: {
    browser,
    confirmationDialog,
    inputDialog,
    basicInfo,
    plans,
    libraryPlans,
    spinnerModal,
    alertDialog,
    globalSnackbar,
    contentPicker,
  },
  state: {
    user: { ...currentUser },
    userOptions: {},
    walls: [],
    communicationSettings: {
      wallCreate: true,
      productUpdate: true,
    },
    wallNotifications: {},
    tenant: { ...currentTenant },
    chatWidgetUserToken: '',
    isApp: device.app,
    isElectronApp: device.electronApp,
    billing: null,
    invoices: [],
    isProcessingDowngrade: false,
    downgradeError: '',
    isProcessingCrossgrade: false,
    crossgradeError: '',
    xDowngradeModal: false,
    downgradeStep: null,
    isLoadingCancellationRefundInformation: false,
    xCrossgradeModal: false,
    crossgradeStep: null,
    isLoadingCrossgradeInformation: false,
    isLoadingGoldDowngradeInformation: false,
    estimatedCrossgradeCost: null,
    estimatedGoldDowngradeCost: null,
    isChrome: device.chrome,
    isFirefox: device.firefox,
    isSafari: device.safari,
    isTouchable: device.touchable,
    isMac: device.mac,
    fetchedCommunicationInformation: false,
    isOnline: true,
    // Library
    libraries: [],
    // Library billing
    isLoadingLibraryBillingInformation: false,
    libraryBilling: null,
    libraryBillingError: '',
    isLoadingLibraryInvoices: false,
    libraryInvoices: [],
    libraryInvoicesError: '',
    xLibraryDowngradeModal: false,
    xClassroomLibraryDowngradeModal: false,
    libraryDowngradeStep: null,
    libraryDowngradeError: '',
    isProcessingLibraryDowngrade: false,
    isLoadingTeamNeonDowngradeInformation: false,
    isLoadingLibraryChangeCardUrl: false,
    estimatedTeamNeonDowngradeRefund: null,
    estimatedCancellationRefund: null,
  },
  // No use declaring return type for getters, mutations, and actions for now
  /* eslint-disable @typescript-eslint/explicit-function-return-type */

  getters: {
    user: (state) => state.user,
    userOptions: (state) => state.userOptions,
    userId: (state): number => state.user.id,
    tenant: (state) => state.tenant,
    tenantName: (state): string => state.tenant.name,
    tenantDomainName: (state): string => state.tenant.domain_name,
    hasEmail: (state): boolean => !!state.user.email,
    isNative: (state) => state.user.tenant_id === 1,
    isOwner: (state) => state.user.role === 'owner',
    isStudent: (state) => state.user.role === 'member',
    isSchool: (state) => state.tenant.type === 'school',
    isBriefcase: (state) => state.tenant.type === 'business',
    isStudentAccountWithoutEmail: (_, getters): boolean => getters.isSchool && getters.isStudent && !getters.hasEmail,
    chatWidgetUserToken: (state) => state.chatWidgetUserToken,
    isApp: (state) => state.isApp,
    billing: (state) => state.billing,
    invoices: (state) => state.invoices,
    isBillingInitialized: (state): boolean => !(state.billing == null),
    currentTenantAdminName: () => backendState.current_tenant_owner_name,
    xDowngradeModal: (state) => state.xDowngradeModal,
    downgradeStep: (state) => state.downgradeStep,
    xCrossgradeModal: (state) => state.xCrossgradeModal,
    crossgradeStep: (state) => state.crossgradeStep,
    dir: () => backendState.dir,
    isNeonTier: (_, getters) => getters.billing?.tier === 'neon',
    isCancellingSoon: (_, getters): boolean => getters?.billing?.status?.is_cancelling_soon,
    hasScheduledChanges: (_, getters): boolean => !!getters.currentScheduledChange,
    currentScheduledChange: (state, getters, _, rootGetters): ScheduledChange | null => {
      if (state.billing?.status?.is_cancelling_soon) return ScheduledChange.DowngradeToNeon
      if (state.billing?.switch_plan_status?.is_switching_soon) {
        if (
          state.billing?.switch_plan_status?.new_plan_id ===
          rootGetters['plans/plans'].gold[normalizedPaymentSchedule(getters.billing.period)].id
        )
          return ScheduledChange.DowngradeToGold
        return state.billing?.period === 'year' ? ScheduledChange.SwitchToMonthly : ScheduledChange.SwitchToAnnual
      }
      return null
    },
    isPaused: (state) => state.billing?.status.is_paused,
    isProcessingDowngrade: (state) => state.isProcessingDowngrade,
    downgradeError: (state) => state.downgradeError,
    isProcessingCrossgrade: (state) => state.isProcessingCrossgrade,
    crossgradeError: (state) => state.crossgradeError,
    isLoadingCrossgradeInformation: (state): boolean => state.isLoadingCrossgradeInformation,
    isLoadingGoldDowngradeInformation: (state): boolean => state.isLoadingGoldDowngradeInformation,
    estimatedCrossgradeCost: (state): EstimateSwitchingCostResult | null => state.estimatedCrossgradeCost,
    estimatedGoldDowngradeCost: (state): EstimateSwitchingCostResult | null => state.estimatedGoldDowngradeCost,
    oppositeSchedulePlan: (_, getters, ___, rootGetters) => {
      return rootGetters['plans/plans'][getters.billing.tier][
        normalizedOppositePaymentSchedule(getters.billing?.period)
      ]
    },
    currentScheduleGoldPlan: (_, getters, ___, rootGetters) =>
      rootGetters['plans/plans'].gold[normalizedPaymentSchedule(getters.billing.period)],
    currentScheduleGoldPlanId: (_, getters) => {
      return getters.currentScheduleGoldPlan.id
    },
    oppositeSchedulePlanId: (_, getters): string => {
      return getters.oppositeSchedulePlan.id
    },
    isWebPlan: (state): boolean => state.billing?.payment_channel === 'chargebee',
    currentOfferedPlan: (_, getters, ___, rootGetters): Plan =>
      rootGetters['plans/plans'][getters.billing.tier][normalizedPaymentSchedule(getters.billing.period)],
    isOfferedPlanCurrencyEqualToUserPlanCurrency: (_, getters): boolean =>
      getters.currentOfferedPlan.currency === getters.billing.plan.currency,
    isOnNewPlan: (_, getters): boolean => getters.currentOfferedPlan.id === getters.billing.plan.plan_id,
    isLegacyTier:
      (_, getters) =>
      (tier): boolean =>
        getters.billing?.tier === tier && !getters.isOnNewPlan && getters.isWebPlan,
    isGoldLegacyTier: (_, getters): boolean => getters.isLegacyTier('gold'),
    isPlatinumLegacyTier: (_, getters): boolean => getters.isLegacyTier('platinum'),
    xUnsubscribeAllText: (state): boolean =>
      !!Object.keys(state.wallNotifications).find((id) => state.wallNotifications[id]),
    pageFilter: (_, getters) => {
      // We only enable the new dashboard settings for native settings and library settings
      if (getters.isNative) {
        const { currentView } = storeToRefs(useSettingsNavigationStore())
        return currentView.value
      }
      return backendState.filter
    },
    fetchedCommunicationInformation: (state): boolean => state.fetchedCommunicationInformation,
    // Library
    libraryId: (): LibraryId | null => {
      const { currentLibraryId } = storeToRefs(useSettingsNavigationStore())
      return currentLibraryId.value
    },
    currentLibrary: (state, getters): Library | null => {
      const library = state.libraries.find((library) => library.id == getters.libraryId)
      return library != null ? library : null
    },
    isClassroomLibrary: (_, getters): boolean => getters.currentLibrary?.libraryType === LibraryType.Classroom,
    isSchoolLibrary: (_, getters): boolean => getters.currentLibrary?.libraryType === LibraryType.School,
    isTeamLibrary: (_, getters): boolean => getters.currentLibrary?.libraryType === LibraryType.Team,
    canViewLibraryInfo: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canViewLibraryInfo,
    canUpdateLibraryInfo: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canUpdateLibraryInfo,
    canViewLibraryMemberships: (_, getters): boolean =>
      getters.currentLibrary?.libraryAccess?.canViewLibraryMemberships,
    canManageMembers: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canManageMembers,
    canManageBilling: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canManageBilling,
    canInviteMembers: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canInviteMembers,
    canInviteSchoolAdmins: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canInviteSchoolAdmins,
    canInviteSchoolTeachers: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canInviteSchoolTeachers,
    canInviteSchoolStudents: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canInviteSchoolStudents,
    canTrashLibrary: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canTrashLibrary,
    isCurrentUserLibraryOwner: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canUseLibraryAsOwner,
    isCurrentUserLibraryAdmin: (_, getters): boolean => getters.currentLibrary?.libraryAccess?.canUseLibraryAsAdmin,
    isCurrentUserAtLeastLibraryTeacher: (_, getters): boolean =>
      getters.currentLibrary?.libraryAccess?.canUseLibraryAsTeacher,
    ownedLibraries: (state): Library[] =>
      state.libraries.filter((library) => !!library.libraryAccess?.canUseLibraryAsOwner),
    // Library billing
    isLoadingLibraryBillingInformation: (state): boolean => state.isLoadingLibraryBillingInformation,
    libraryBilling: (state): LibraryBilling | null => state.libraryBilling,
    libraryBillingError: (state): string => state.libraryBillingError,
    isLoadingLibraryInvoices: (state): boolean => state.isLoadingLibraryInvoices,
    libraryInvoices: (state): LibraryInvoice[] => state.libraryInvoices,
    libraryInvoicesError: (state): string => state.libraryInvoicesError,
    libraryName: (_, getters): string => getters.currentLibrary?.name ?? '',
    librarySlug: (_, getters): string => getters.currentLibrary?.slug ?? '',
    libraryMakersCount: (_, getters): number => getters.currentLibrary?.makersCount ?? 0,
    libraryUserQuota: (_, getters): LibraryUserQuotaInfo | undefined => getters.currentLibrary?.userQuota,
    libraryUsersBilledCount: (_, getters): number =>
      isPaidLibraryBilling(getters.libraryBilling) ? getters.libraryBilling.users_billed_count : 0,
    hasPendingMakerCountUpdate: (_, getters): boolean =>
      !!getters.libraryUsersBilledCount && getters.libraryUsersBilledCount !== getters.libraryMakersCount,
    libraryWallsUsed: (_, getters): number => getters.currentLibrary?.quota?.wallsUsed ?? 0,
    isLibrarySchool: (_, getters): boolean => getters.currentLibrary?.libraryType === LibraryType.School,
    isLibraryPaying: (_, getters): boolean => getters.isLibraryTeamGoldTier,
    isLibraryTeamTrialTier: (_, getters): boolean =>
      (getters.isTeamLibrary as boolean) && getters.libraryBilling?.tier === LibraryMembershipTier.Trial,
    isLibraryTeamNeonTier: (_, getters): boolean =>
      (getters.isTeamLibrary as boolean) && getters.libraryBilling?.tier === LibraryMembershipTier.Neon,
    isLibraryTeamGoldTier: (_, getters): boolean =>
      (getters.isTeamLibrary as boolean) && getters.libraryBilling?.tier === LibraryMembershipTier.Gold,
    isLibraryClassroomGoldTier: (_, getters): boolean =>
      (getters.isClassroomLibrary as boolean) && getters.libraryBilling?.tier === LibraryMembershipTier.Gold,
    isLibraryWebPlan: (_, getters): boolean => getters.libraryBilling?.payment_channel === 'chargebee',
    isOfferedTeamCurrencyEqualToCurrentCurrency: (_, getters, ___, rootGetters): boolean => {
      if (!getters.isLibraryPaying) return true
      return (
        (useLibraryPlansStore().plans.gold.annual.currency?.toLowerCase() ??
          rootGetters['libraryPlans/plans'].gold.annual.currency?.toLowerCase()) ===
        getters.libraryBilling?.plan?.currency?.toLowerCase()
      )
    },

    currentLibraryScheduledChange: (_, getters): LibraryScheduledChange | null => {
      if (!isPaidLibraryBilling(getters.libraryBilling)) return null
      if (getters.libraryBilling.status.is_cancelling_soon === true) return LibraryScheduledChange.DowngradeToTeamNeon
      if (getters.libraryBilling.switch_plan_status.is_switching_soon === true) {
        return getters.libraryBilling.period === 'year'
          ? LibraryScheduledChange.SwitchToMonthly
          : LibraryScheduledChange.SwitchToAnnual
      }
      return null
    },
    hasLibraryScheduledChanges: (_, getters): boolean => !!getters.currentLibraryScheduledChange,
    xLibraryDowngradeModal: (state): boolean => state.xLibraryDowngradeModal,
    xClassroomLibraryDowngradeModal: (state): boolean => state.xClassroomLibraryDowngradeModal,
    libraryDowngradeStep: (state): LibraryDowngradeStep | null => state.libraryDowngradeStep,
    isLoadingCancellationRefundInformation: (state): boolean => state.isLoadingCancellationRefundInformation,
    libraryDowngradeError: (state): string => state.libraryDowngradeError,
    isProcessingLibraryDowngrade: (state): boolean => state.isProcessingLibraryDowngrade,
    estimatedCancellationRefund: (state): EstimateCancellationRefundResult | null => state.estimatedCancellationRefund,
    estimatedCancellationRefundAmount: (_, getters): string | null => {
      if (!getters.estimatedCancellationRefund) return null
      return translatePrice(
        parseFloat(getters.estimatedCancellationRefund.amount),
        getters.estimatedCancellationRefund.currency,
      )
    },
    estimatedTeamNeonDowngradeRefund: (state): EstimateTeamNeonDowngradeRefund | null =>
      state.estimatedTeamNeonDowngradeRefund,
    estimatedTeamNeonDowngradeRefundAmount: (_, getters): string | null => {
      if (!getters.estimatedTeamNeonDowngradeRefund) return null
      return translatePrice(
        parseFloat(getters.estimatedTeamNeonDowngradeRefund.amount),
        getters.estimatedTeamNeonDowngradeRefund.currency,
      )
    },
    isLoadingTeamNeonDowngradeInformation: (state): boolean => state.isLoadingTeamNeonDowngradeInformation,
    isLoadingLibraryChangeCardUrl: (state): boolean => state.isLoadingLibraryChangeCardUrl,
    libraries: (state): Library[] => sortBy(state.libraries, 'joinedAt'),
    trialDaysLeft: (_, getters): number => getters.currentLibrary?.trialDaysLeft ?? 0,
    librarySubscriptionStatus: (_, getters): LibrarySubscriptionStatus | null => {
      if (getters.currentLibrary == null) return null
      if (getters.currentLibrary.isDeprecatedFreeTier === true) return null
      if (getters.currentLibrary.membershipTier === LibraryMembershipTier.Trial)
        return LibrarySubscriptionStatus.OnTrial
      if (getters.currentLibrary.isTrialExpired === true) return LibrarySubscriptionStatus.TrialExpired
      if (getters.currentLibrary.isPlanCancelled === true) return LibrarySubscriptionStatus.Cancelled
      if (getters.currentLibrary.membershipTier === LibraryMembershipTier.Gold) return LibrarySubscriptionStatus.Paying
      return null
    },
  },
  mutations: {
    INITIALIZE_STATE(state, data) {
      state.user = data.user
      state.userOptions = data.userOptions
      state.libraries = data.libraries
      state.chatWidgetUserToken = data.chatWidgetUserToken
    },
    FETCHED_BILLING(state, billing) {
      state.billing = billing
    },
    FETCHED_INVOICES(state, invoices) {
      state.invoices = invoices
    },
    SHOW_PLAN_DOWNGRADE_MODAL(state, step: DowngradeStep) {
      state.xDowngradeModal = true
      state.downgradeStep = step
    },
    CLOSE_PLAN_DOWNGRADE_MODAL(state) {
      state.xDowngradeModal = false
      state.downgradeStep = null
      state.isProcessingDowngrade = false
      state.downgradeError = ''
      state.isLoadingGoldDowngradeInformation = false
      state.estimatedGoldDowngradeCost = null
    },
    SHOW_PLAN_CROSSGRADE_MODAL(state, step: CrossgradeStep) {
      state.xCrossgradeModal = true
      state.crossgradeStep = step
    },
    CLOSE_PLAN_CROSSGRADE_MODAL(state) {
      state.xCrossgradeModal = false
      state.crossgradeStep = null
      state.isProcessingCrossgrade = false
      state.crossgradeError = ''
      state.isLoadingCrossgradeInformation = false
      state.estimatedCrossgradeCost = null
    },
    UPDATE_CURRENT_TENANT(state, tenant: Partial<Tenant>): void {
      state.tenant = merge({}, state.tenant, tenant)
    },
    UNDO_SCHEDULED_DOWNGRADE(state) {
      if (state.billing != null) {
        state.billing.status.is_cancelling_soon = false
      }
    },
    UNDO_SCHEDULED_SWITCH(state) {
      if (state.billing != null) {
        state.billing.switch_plan_status.is_switching_soon = false
        state.billing.switch_plan_status.new_plan_id = ''
      }
    },
    SET_ESTIMATED_CANCELLATION_REFUND(state, estimatedCancellationRefund: EstimateCancellationRefundResult): void {
      state.estimatedCancellationRefund = estimatedCancellationRefund
    },
    SET_IS_LOADING_REFUND_INFORMATION(state, isLoadingCancellationRefundInformation: boolean): void {
      state.isLoadingCancellationRefundInformation = isLoadingCancellationRefundInformation
    },
    SET_IS_PROCESSING_DOWNGRADE(state, isProcessingDowngrade) {
      state.isProcessingDowngrade = isProcessingDowngrade
    },
    SET_DOWNGRADE_ERROR(state, downgradeError) {
      state.downgradeError = downgradeError
    },
    SET_IS_PROCESSING_CROSSGRADE(state, isProcessingCrossgrade) {
      state.isProcessingCrossgrade = isProcessingCrossgrade
    },
    SET_CROSSGRADE_ERROR(state, crossgradeError) {
      state.crossgradeError = crossgradeError
    },
    STARTED_LOADING_CROSSGRADE_INFORMATION(state) {
      state.isLoadingCrossgradeInformation = true
    },
    DONE_LOADING_CROSSGRADE_INFORMATION(state) {
      state.isLoadingCrossgradeInformation = false
    },
    STARTED_LOADING_GOLD_DOWNGRADE_INFORMATION(state) {
      state.isLoadingGoldDowngradeInformation = true
    },
    DONE_LOADING_GOLD_DOWNGRADE_INFORMATION(state) {
      state.isLoadingGoldDowngradeInformation = false
    },
    SET_ESTIMATED_CROSSGRADE_COST(state, estimatedCrossgradeCost) {
      state.estimatedCrossgradeCost = estimatedCrossgradeCost
    },
    SET_ESTIMATED_GOLD_DOWNGRADE_COST(state, estimatedGoldDowngradeCost) {
      state.estimatedGoldDowngradeCost = estimatedGoldDowngradeCost
    },
    SET_USER_WALLS(state, walls: Wall[]): void {
      state.walls = walls
    },
    SET_COMMUNICATION_SETTINGS(state, settings: CommunicationSetting): void {
      state.communicationSettings = { ...state.communicationSettings, ...settings }
    },
    SET_WALL_NOTIFICATIONS(state, wallNotifications: Record<string, boolean>): void {
      state.wallNotifications = { ...state.wallNotifications, ...wallNotifications }
    },
    SET_FETCHED_COMMUNICATION_INFORMATION(state, fetchedCommunicationInformation: boolean): void {
      state.fetchedCommunicationInformation = fetchedCommunicationInformation
    },
    UNDO_LIBRARY_SCHEDULED_DOWNGRADE(state): void {
      if (isPaidLibraryBilling(state.libraryBilling)) {
        state.libraryBilling.status.is_cancelling_soon = false
      }
    },
    FETCHED_LIBRARY_BILLING(state, libraryBilling): void {
      state.libraryBilling = libraryBilling
    },
    SET_IS_LOADING_LIBRARY_BILLING_INFORMATION(state, isLoadingLibraryBillingInformation: boolean): void {
      state.isLoadingLibraryBillingInformation = isLoadingLibraryBillingInformation
    },
    SET_LIBRARY_BILLING_ERROR(state, libraryBillingError: string): void {
      state.libraryBillingError = libraryBillingError
    },
    FETCHED_LIBRARY_INVOICES(state, libraryInvoices: LibraryInvoice[]): void {
      state.libraryInvoices = libraryInvoices
    },
    SET_IS_LOADING_LIBRARY_INVOICES(state, isLoadingLibraryInvoices: boolean): void {
      state.isLoadingLibraryInvoices = isLoadingLibraryInvoices
    },
    SET_LIBRARY_INVOICES_ERROR(state, libraryInvoicesError: string): void {
      state.libraryInvoicesError = libraryInvoicesError
    },
    UNDO_LIBRARY_SCHEDULED_SWITCH(state): void {
      if (isPaidLibraryBilling(state.libraryBilling)) {
        state.libraryBilling.switch_plan_status.is_switching_soon = false
        state.libraryBilling.switch_plan_status.new_plan_id = ''
      }
    },
    SHOW_LIBRARY_DOWNGRADE_MODAL(state, step: LibraryDowngradeStep): void {
      state.xLibraryDowngradeModal = true
      state.libraryDowngradeStep = step
      state.isProcessingLibraryDowngrade = false
      state.libraryDowngradeError = ''
    },
    SHOW_CLASSROOM_LIBRARY_DOWNGRADE_MODAL(state, step: LibraryDowngradeStep): void {
      state.xClassroomLibraryDowngradeModal = true
      state.libraryDowngradeStep = step
      state.isProcessingLibraryDowngrade = false
      state.libraryDowngradeError = ''
    },
    CLOSE_LIBRARY_DOWNGRADE_MODAL(state): void {
      state.xLibraryDowngradeModal = false
      state.libraryDowngradeStep = null
    },
    CLOSE_CLASSROOM_LIBRARY_DOWNGRADE_MODAL(state): void {
      state.xClassroomLibraryDowngradeModal = false
      state.libraryDowngradeStep = null
    },
    CHANGE_STEP_LIBRARY_DOWNGRADE_MODAL(state, libraryDowngradeStep: LibraryDowngradeStep): void {
      state.libraryDowngradeStep = libraryDowngradeStep
      state.isProcessingLibraryDowngrade = false
      state.libraryDowngradeError = ''
    },
    SET_IS_PROCESSING_LIBRARY_DOWNGRADE(state, isProcessingLibraryDowngrade: boolean): void {
      state.isProcessingLibraryDowngrade = isProcessingLibraryDowngrade
    },
    SET_LIBRARY_DOWNGRADE_ERROR(state, libraryDowngradeError: string): void {
      state.libraryDowngradeError = libraryDowngradeError
    },
    SET_IS_LOADING_TEAM_NEON_DOWNGRADE_INFORMATION(state, isLoadingTeamNeonDowngradeInformation: boolean): void {
      state.isLoadingTeamNeonDowngradeInformation = isLoadingTeamNeonDowngradeInformation
    },
    SET_ESTIMATED_TEAM_NEON_DOWNGRADE_COST(
      state,
      estimatedTeamNeonDowngradeRefund: EstimateTeamNeonDowngradeRefund,
    ): void {
      state.estimatedTeamNeonDowngradeRefund = estimatedTeamNeonDowngradeRefund
    },
    SET_IS_LOADING_LIBRARY_CHANGE_CARD_URL(state, isloadingLibraryChangeCardUrl: boolean): void {
      state.isLoadingLibraryChangeCardUrl = isloadingLibraryChangeCardUrl
    },
    CHANGE_CONNECTIVITY(state, isOnline: boolean) {
      state.isOnline = isOnline
    },
    SET_LIBRARIES(state, libraries: Library[]) {
      state.libraries = libraries
    },
    UPDATE_USER_OPTIONS(state, userOptions) {
      state.userOptions = { ...state.userOptions, ...userOptions } // overrides fields present in both objects
    },
  },
  actions: {
    async initializeState({ dispatch, getters }): Promise<void> {
      dispatch('browser/initializeState')
      const { arvoConfig, ...initialState } = await fetchJson('/dashboard.json')
      setScope({ userId: initialState.user.id })
      configureArvo(arvoConfig)
      dispatch('initializePageState', initialState)
      if (getters.isNative === true) {
        void dispatch('fetchViewableLibraries')
      }
      if (isAppUsing('googleAppLicensing')) {
        void dispatch('validateGoogleAppLicense')
      }
    },
    initializePageState({ state, getters, commit, dispatch }, fetchedState): void {
      commit('INITIALIZE_STATE', fetchedState)
      switch (getters.pageFilter) {
        case 'basic':
          void useSettingsUserInfoStore().fetchUserInfo()
          break
        case 'dashboard':
          void useSettingsUserDashboardStore().initialize()
          break
        case 'communication':
          dispatch('initializeCommunicationSettings')
          break
        case 'org_basic':
          useSettingsTenantInfoStore().initialize()
          break
        case 'developers':
          void useSettingsDevelopersStore().fetchApiToken()
          break
        case 'billing':
          dispatch('initializeBillingSettings')
          break
        case 'delete':
          dispatch('initializeBillingSettings')
          break
        case 'org_permissions':
          if (getters.isBriefcase as boolean) {
            useSettingsBriefcasePermissionsStore().initialize(fetchedState.tenant)
          } else {
            useSettingsBackpackPermissionsStore().initialize(fetchedState.tenant)
          }
          break
        case 'org_content_safety':
          if (getters.isSchool as boolean) {
            useContentSafetySettingsStore().initializeState(ContentSafetyEntity.Tenant, fetchedState.tenant.id ?? '')
          }
          break
        case 'org_analytics':
          useSettingsTenantAnalyticsStore().initialize(fetchedState.tenant)
          break
        case 'library_info':
          void useSettingsLibraryInfoStore().fetchLibraryInfo()
          break
        case 'library_security':
          useSettingsLibrarySecurityStore().initialize()
          break
        case 'library_members':
          void useSettingsLibraryMembersStore().fetchLibraryMemberships()
          break
        case 'library_content_safety_settings':
          useContentSafetySettingsStore().initializeState(ContentSafetyEntity.Library, getters.currentLibrary?.id)
          break
        case 'library_billing':
          dispatch('initializeLibraryBillingSettings')
          break
        case 'library_delete':
          dispatch('initializeLibraryDeleteSettings')
          break
        case 'library_permissions':
          useSettingsSchoolPermissionsStore().initialize(getters.currentLibrary)
          break
        case 'notifications':
          useSettingsNotificationsStore().initialize()
          break
        case SettingView.Integrations:
          useSettingsIntegrationPermissionsStore().initialize()
      }
    },
    initializeBillingSettings({ dispatch }): void {
      dispatch('plans/fetchPlans')
      dispatch('plans/fetchAdvertisedQuotas')
      void usePersonalPlansStore().initialize()
      dispatch('fetchBilling')
      dispatch('fetchInvoices')
    },
    async initializeCommunicationSettings({ commit, dispatch }): Promise<void> {
      try {
        await dispatch('fetchUserWalls')
        await dispatch('fetchCommunicationSettings')
      } catch (e) {
        captureFetchException(e, { source: 'DashboardSettingsInitializeCommunicationSettings' })
      } finally {
        commit('SET_FETCHED_COMMUNICATION_INFORMATION', true)
      }
    },
    async fetchBilling({ commit }) {
      const response = await PadletApi.Billing.fetch()
      commit('FETCHED_BILLING', response.data)
    },
    async fetchInvoices({ commit }) {
      const response = await PadletApi.Invoice.fetch()
      commit('FETCHED_INVOICES', response.data)
    },
    handleRefundEstimationErrorInDowngrade({ dispatch }, { e }): void {
      e.status === 422
        ? dispatch('setDowngradeRequestError', {
            errorMessage: unpaidInvoiceErrorMessage,
          })
        : dispatch('setDowngradeRequestError', {})
    },
    handleRefundEstimationErrorInCrossgrade({ dispatch }, { e }): void {
      e.status === 422
        ? dispatch('setCrossgradeRequestError', {
            errorMessage: unpaidInvoiceErrorMessage,
          })
        : dispatch('setCrossgradeRequestError', {})
    },
    async fetchRefunds({ commit, dispatch }, { errorHandlerAction }: { errorHandlerAction: string }): Promise<void> {
      commit('SET_IS_LOADING_REFUND_INFORMATION', true)
      try {
        const response: JsonAPIResponse<EstimateCancellationRefundResult> =
          await PadletApi.Billing.estimateCancellationRefund()
        const estimatedRefund = (response?.data as JsonAPIResource<EstimateCancellationRefundResult>)?.attributes
        commit('SET_ESTIMATED_CANCELLATION_REFUND', estimatedRefund)
      } catch (e) {
        dispatch(errorHandlerAction, { e })
      } finally {
        commit('SET_IS_LOADING_REFUND_INFORMATION', false)
      }
    },
    showPlanDowngradeToNeonModalCheckingQuota(
      { dispatch },
      { tierQuota, wallsUsed }: { tierQuota: number; wallsUsed: number },
    ) {
      if (wallsUsed > tierQuota) {
        dispatch('showAboveNeonQuotaConfirmationDialog', {
          wallsUsed,
          tierQuota,
        })
        return
      }
      dispatch('showPlanDowngradeToNeonModal')
    },
    showPlanDowngradeToGoldModalCheckingQuota(
      { commit, getters, dispatch },
      { tierQuota, wallsUsed, userPlanCurrency }: { tierQuota: number; wallsUsed: number; userPlanCurrency: string },
    ) {
      const isDowngradingToSameCurrency = userPlanCurrency === getters.currentScheduleGoldPlan.currency
      if (isDowngradingToSameCurrency) {
        dispatch('fetchEstimatedGoldDowngradeCost')
        if (wallsUsed > tierQuota) {
          dispatch('showAboveGoldQuotaConfirmationDialog', {
            wallsUsed,
            tierQuota,
            afterConfirmAction: 'showPlanDowngradeToGoldModal',
          })
          return
        }
        dispatch('showPlanDowngradeToGoldModal')
      } else {
        if (wallsUsed > tierQuota) {
          dispatch('showAboveGoldQuotaConfirmationDialog', {
            wallsUsed,
            tierQuota,
            afterConfirmAction: 'showPlanDowngradeToGoldDifferentCurrencyModal',
          })
          return
        }
        dispatch('showPlanDowngradeToGoldDifferentCurrencyModal')
      }
    },
    showPlanDowngradeModal({ commit }, step: DowngradeStep) {
      commit('SHOW_PLAN_DOWNGRADE_MODAL', step)
    },
    showAboveQuotaConfirmationDialog(
      { rootGetters, dispatch },
      {
        title,
        body,
        afterConfirmActionType,
      }: {
        title: string
        body: string
        afterConfirmActionType: string
      },
    ): void {
      dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          ...THINKING_FACE_ICON,
          layoutVariant: useWindowSizeStore().isPhone
            ? ConfirmationDialogLayoutVariant.Drawer
            : ConfirmationDialogLayoutVariant.Large,
          title,
          body,
          confirmButtonText: __('Continue downgrading'),
          cancelButtonText: __('Nevermind'),
          afterConfirmActions: [{ type: afterConfirmActionType }],
        },
        { root: true },
      )
    },
    showAboveNeonQuotaConfirmationDialog(
      { dispatch },
      { wallsUsed, tierQuota }: { wallsUsed: number; tierQuota: number },
    ): void {
      void dispatch('showAboveQuotaConfirmationDialog', {
        title: __('You’re above this tier’s padlet limit'),
        body: isAppUsing('freezePadlet')
          ? __(
              'You currently have %{amount} padlets, but our Neon tier only supports %{neonQuota}. You will keep your existing padlets but you won’t be able to create new ones. In 7 days your existing padlets will be frozen until you are at or below the padlet limit. Learn more.',
              {
                amount: wallsUsed,
                neonQuota: tierQuota,
              },
            )
          : __(
              'You currently have %{amount} padlets, but our Neon tier only supports %{neonQuota}. You won’t lose anything by downgrading but creating and editing padlets may be limited until you remove existing ones.',
              {
                amount: wallsUsed,
                neonQuota: tierQuota,
              },
            ),
        afterConfirmActionType: 'showPlanDowngradeToNeonModal',
      })
    },
    showAboveGoldQuotaConfirmationDialog(
      { dispatch },
      {
        wallsUsed,
        tierQuota,
        afterConfirmAction,
      }: { wallsUsed: number; tierQuota: number; afterConfirmAction: string },
    ): void {
      void dispatch('showAboveQuotaConfirmationDialog', {
        title: __('You’re above this tier’s padlet limit'),
        body: isAppUsing('freezePadlet')
          ? __(
              'You currently have %{amount} padlets, but our Gold tier only supports %{goldQuota}. You will keep your existing padlets but you won’t be able to create new ones. In 7 days your existing padlets will be frozen until you are at or below the padlet limit. Learn more.',
              {
                amount: wallsUsed,
                goldQuota: tierQuota,
              },
            )
          : __(
              'You currently have %{amount} padlets, but our Gold tier only supports %{goldQuota}. You won’t lose anything by downgrading but creating and editing padlets may be limited until you remove existing ones.',
              {
                amount: wallsUsed,
                goldQuota: tierQuota,
              },
            ),
        afterConfirmActionType: afterConfirmAction,
      })
    },
    showPlanDowngradeToNeonModal({ commit, dispatch }): void {
      dispatch('fetchRefunds', { errorHandlerAction: 'handleRefundEstimationErrorInDowngrade' })
      commit('SHOW_PLAN_DOWNGRADE_MODAL', GenericStep.ChooseDate)
    },
    showPlanDowngradeToGoldModal({ commit }): void {
      commit('SHOW_PLAN_DOWNGRADE_MODAL', GenericStep.ChooseDateGold)
    },
    showPlanDowngradeToGoldDifferentCurrencyModal({ commit, dispatch }): void {
      dispatch('fetchRefunds', { errorHandlerAction: 'handleRefundEstimationErrorInDowngrade' })
      commit('SHOW_PLAN_DOWNGRADE_MODAL', GenericStep.NowGoldDifferentCurrency)
    },
    closePlanDowngradeModal({ commit }) {
      commit('CLOSE_PLAN_DOWNGRADE_MODAL')
    },
    showPlanCrossgradeModal({ commit }, step: CrossgradeStep) {
      commit('SHOW_PLAN_CROSSGRADE_MODAL', step)
    },
    closePlanCrossgradeModal({ commit }) {
      commit('CLOSE_PLAN_CROSSGRADE_MODAL')
    },
    showPlanCrossgradeModalCheckingCurrency({ getters, dispatch }, { userPlanCurrency }: { userPlanCurrency: string }) {
      const isSwitchingToSameCurrency = userPlanCurrency === getters.oppositeSchedulePlan.currency
      if (isSwitchingToSameCurrency) {
        dispatch('fetchEstimatedCrossgradeCost')
        dispatch('showPlanCrossgradeModal', GenericStep.ChooseDate)
        return
      }
      dispatch('fetchRefunds', { errorHandlerAction: 'handleRefundEstimationErrorInCrossgrade' })
      dispatch('showPlanCrossgradeModal', GenericStep.NowDifferentCurrency)
    },
    showScheduledChangeModal({ getters, dispatch }): void {
      const showConfirmationDialog = async ({ title, body }) =>
        await dispatch('confirmationDialog/openConfirmationDialog', {
          ...THINKING_FACE_ICON,
          title,
          body,
          confirmButtonText: __('Confirm'),
          cancelButtonText: __('Nevermind'),
          afterConfirmActions: [{ type: 'undoScheduledChange' }],
          forceFullWidthButtons: true,
        })
      if (getters.currentScheduledChange === ScheduledChange.DowngradeToNeon) {
        if (getters.billing.tier === 'platinum') {
          showConfirmationDialog({
            title: __('Cancel downgrade to Neon?'),
            body: __('You will continue to pay for your Platinum membership.'),
          })
        } else if (getters.billing.tier === 'gold') {
          showConfirmationDialog({
            title: __('Cancel downgrade to Neon?'),
            body: __('You will continue to pay for your Gold membership.'),
          })
        }
      } else if (getters.currentScheduledChange === ScheduledChange.DowngradeToGold) {
        showConfirmationDialog({
          title: __('Cancel downgrade to Gold?'),
          body: __('You will continue to pay for your Platinum membership.'),
        })
      } else if (getters.currentScheduledChange === ScheduledChange.SwitchToMonthly) {
        showConfirmationDialog({
          title: __('Cancel change to monthly payments?'),
          body: __('You will continue to pay once per year.'),
        })
      } else if (getters.currentScheduledChange === ScheduledChange.SwitchToAnnual) {
        showConfirmationDialog({
          title: __('Cancel change to annual payments?'),
          body: __('You will continue to pay once per month.'),
        })
      }
    },
    showFailedUndoScheduledChangeDialog(
      { dispatch },
      { afterConfirmActionType }: { afterConfirmActionType: string },
    ): void {
      dispatch('confirmationDialog/openConfirmationDialog', {
        ...ALERT_ICON,
        title: __('Undo failed'),
        body: __('Oops! Something went wrong. We were unable to process your undo request.'),
        confirmButtonText: __('Try again'),
        cancelButtonText: __('Cancel'),
        afterConfirmActions: [{ type: afterConfirmActionType }],
        forceFullWidthButtons: true,
        shouldFadeIn: false,
      })
    },
    async undoScheduledChange({ getters, commit, dispatch }): Promise<void> {
      const scheduledChange: ScheduledChange = getters.currentScheduledChange
      dispatch('spinnerModal/openSpinnerModal')
      let response: UndoScheduledChangeResult = { success: false, errors: [] }
      try {
        if (scheduledChange === ScheduledChange.DowngradeToNeon) {
          response = await PadletApi.Billing.undoScheduledCancellation()
        } else if (scheduledChange === ScheduledChange.DowngradeToGold) {
          response = await PadletApi.Billing.undoScheduledSwitch() // Downgrades between 2 premium plans are treated as switches in the backend
        } else if (
          scheduledChange === ScheduledChange.SwitchToAnnual ||
          scheduledChange === ScheduledChange.SwitchToMonthly
        ) {
          response = await PadletApi.Billing.undoScheduledSwitch()
        }
        if (!response.success || response.errors?.length) {
          dispatch('showFailedUndoScheduledChangeDialog', { afterConfirmActionType: 'undoScheduledChange' })
          return
        }
        const successUndoMutations = {
          [ScheduledChange.DowngradeToNeon]: 'UNDO_SCHEDULED_DOWNGRADE',
          [ScheduledChange.DowngradeToGold]: 'UNDO_SCHEDULED_SWITCH', // Downgrades between 2 premium plans are treated as switches in the backend
          [ScheduledChange.SwitchToMonthly]: 'UNDO_SCHEDULED_SWITCH',
          [ScheduledChange.SwitchToAnnual]: 'UNDO_SCHEDULED_SWITCH',
        }
        commit(successUndoMutations[scheduledChange])
      } catch {
        dispatch('showFailedUndoScheduledChangeDialog', { afterConfirmActionType: 'undoScheduledChange' })
      } finally {
        dispatch('spinnerModal/closeSpinnerModal')
      }
    },
    goBackToSettingsMenu() {
      useSettingsNavigationStore().goBackToSettingsMenu()
    },
    goBack(): void {
      useSettingsNavigationStore().goBackToReferrerOrDashboard()
    },
    switchCurrentView(_, payload: { view: SettingView; libraryId?: LibraryId }) {
      useSettingsNavigationStore().switchCurrentView(payload)
    },
    reloadPage() {
      reload()
    },
    async fetchEstimatedGoldDowngradeCost({ commit, dispatch, getters }): Promise<void> {
      commit('STARTED_LOADING_GOLD_DOWNGRADE_INFORMATION')
      const body = asciiSafeStringify({
        plan_id: getters.currentScheduleGoldPlanId,
      })
      try {
        commit('SET_ESTIMATED_GOLD_DOWNGRADE_COST', await PadletApi.Billing.estimateSwitchingCost({ body }))
      } catch (e) {
        e.status === 422
          ? dispatch('setDowngradeRequestError', {
              errorMessage: unpaidInvoiceErrorMessage,
            })
          : dispatch('setDowngradeRequestError', {})
      }
      commit('DONE_LOADING_GOLD_DOWNGRADE_INFORMATION')
    },
    async fetchEstimatedCrossgradeCost({ getters, commit, dispatch }): Promise<void> {
      commit('STARTED_LOADING_CROSSGRADE_INFORMATION')
      const body = asciiSafeStringify({
        plan_id: getters.oppositeSchedulePlanId,
      })
      try {
        commit('SET_ESTIMATED_CROSSGRADE_COST', await PadletApi.Billing.estimateSwitchingCost({ body }))
      } catch (e) {
        e.status === 422
          ? dispatch('setCrossgradeRequestError', {
              errorMessage: unpaidInvoiceErrorMessage,
            })
          : dispatch('setCrossgradeRequestError', {})
      }
      commit('DONE_LOADING_CROSSGRADE_INFORMATION')
    },
    cancelImmediately({ dispatch }): void {
      dispatch('downgradeAccount', CancellationType.EndSubscription)
    },
    doNotRenew({ dispatch }): void {
      dispatch('downgradeAccount', CancellationType.SetAsNonRenewing)
    },
    async downgradeAccount({ commit, dispatch, getters }, cancellationType: CancellationType) {
      try {
        commit('SET_IS_PROCESSING_DOWNGRADE', true)
        const body = asciiSafeStringify({ cancellation_type: cancellationType })
        const {
          links: { status },
        } = await PadletApi.Billing.cancelMembership({ body })
        const response = await poll({
          pollingUrl: status,
          validationCallback: (response) => response.status && response.status !== 'active',
          options: { intervalSecs: 5, maxAttempts: 10 },
        })
        // Check if refund processed
        if (getters.downgradeStep === GenericStep.Now) {
          // Only show refund issue message if the account is not paused.
          if (!response.refunded && !getters.isPaused) {
            dispatch('closePlanDowngradeModal')
            dispatch('alertDialog/openAlertDialog', {
              ...ALERT_ICON,
              title: __('Refund failed!'),
              body: __(
                'We were unable to process your refund. Our support team will be in contact within 24 hours. Your refund amount will not be affected.',
              ),
              closeButtonText: __('Okay'),
              afterCloseActions: [{ type: 'reloadPage' }],
            })
          } else {
            commit('SHOW_PLAN_DOWNGRADE_MODAL', GenericStep.ConfirmNow)
          }
        } else if (getters.downgradeStep === GenericStep.Later) {
          commit('SHOW_PLAN_DOWNGRADE_MODAL', GenericStep.ConfirmLater)
        }
      } catch (e) {
        dispatch('setDowngradeRequestError', {})
      } finally {
        commit('SET_IS_PROCESSING_DOWNGRADE', false)
      }
    },
    downgradePlatinumToGoldImmediately({ getters, dispatch }): void {
      dispatch('downgradeAccountPlatinumToGold', {
        switchType: SwitchType.SwitchImmediately,
        planId: getters.currentScheduleGoldPlanId,
      })
    },
    schedulePlatinumToGoldDowngrade({ getters, dispatch }): void {
      dispatch('downgradeAccountPlatinumToGold', {
        switchType: SwitchType.SwitchAtEndOfTerm,
        planId: getters.currentScheduleGoldPlanId,
      })
    },
    async downgradeAccountPlatinumToGold(
      { commit, getters, dispatch },
      { switchType, planId }: { switchType: SwitchType; planId: string },
    ) {
      try {
        commit('SET_IS_PROCESSING_DOWNGRADE', true)
        const body = asciiSafeStringify({
          switch_type: switchType,
          plan_id: planId,
        })
        const {
          links: { status },
        } = await PadletApi.Billing.switchPlan({ body })
        const response = await poll({
          pollingUrl: status,
          validationCallback: (response) => response.hasOwnProperty('switch_status'),
          options: { intervalSecs: 5, maxAttempts: 10 },
        })
        if (response.switch_status.error_message) {
          dispatch('setDowngradeRequestError', {
            errorMessage: response.switch_status.error_message,
          })
        } else if (!response.switch_status.is_switched) {
          dispatch('setDowngradeRequestError', {})
        } else if (response.switch_status.hasOwnProperty('is_refunded') && !response.switch_status.is_refunded) {
          dispatch('closePlanDowngradeModal')
          dispatch('alertDialog/openAlertDialog', {
            ...ALERT_ICON,
            title: __('Refund failed!'),
            body: __(
              'You have successfully switched plans! Unfortunately, your bank is unable to process your refund. We have extended your term end for now.',
            ),
            closeButtonText: __('Okay'),
            afterCloseActions: [{ type: 'reloadPage' }],
          })
        } else if (getters.downgradeStep === GenericStep.NowGold) {
          commit('SHOW_PLAN_DOWNGRADE_MODAL', GenericStep.ConfirmNowGold)
        } else if (getters.downgradeStep === GenericStep.LaterGold) {
          commit('SHOW_PLAN_DOWNGRADE_MODAL', GenericStep.ConfirmLaterGold)
        }
      } catch (e) {
        dispatch('setDowngradeRequestError', {})
      } finally {
        commit('SET_IS_PROCESSING_DOWNGRADE', false)
      }
    },
    async downgradePlatinumToGoldDifferentCurrencyImmediately({ commit, getters, dispatch }): Promise<void> {
      try {
        commit('SET_IS_PROCESSING_DOWNGRADE', true)
        const body = asciiSafeStringify({
          plan_id: getters.currentScheduleGoldPlanId,
        })
        await PadletApi.Billing.switchCurrency({ body })
        commit('SHOW_PLAN_DOWNGRADE_MODAL', GenericStep.ConfirmNowGoldDifferentCurrency)
      } catch (e) {
        try {
          if (JSON.parse(e.message).error === 'refund_failed') {
            dispatch('closePlanDowngradeModal')
            dispatch('alertDialog/openAlertDialog', {
              ...ALERT_ICON,
              title: __('Refund failed!'),
              body: __(
                'Sorry, we were unable to refund your previous subscription through your card. We have extended your current subscription.',
              ),
              closeButtonText: __('Okay'),
              afterCloseActions: [{ type: 'reloadPage' }],
            })
          } else {
            dispatch('setDowngradeRequestError', { errorMessage: JSON.parse(e.message).error })
          }
        } catch {
          dispatch('setDowngradeRequestError', {})
        }
      } finally {
        commit('SET_IS_PROCESSING_DOWNGRADE', false)
      }
    },
    switchImmediately({ getters, dispatch }): void {
      dispatch('switchAccount', { switchType: SwitchType.SwitchImmediately, planId: getters.oppositeSchedulePlanId })
    },
    scheduleSwitch({ getters, dispatch }): void {
      dispatch('switchAccount', { switchType: SwitchType.SwitchAtEndOfTerm, planId: getters.oppositeSchedulePlanId })
    },
    async switchAccount(
      { commit, getters, dispatch },
      { switchType, planId }: { switchType: SwitchType; planId: string },
    ) {
      try {
        commit('SET_IS_PROCESSING_CROSSGRADE', true)
        const body = asciiSafeStringify({
          switch_type: switchType,
          plan_id: planId,
        })
        const {
          links: { status },
        } = await PadletApi.Billing.switchPlan({ body })
        const response = await poll({
          pollingUrl: status,
          validationCallback: (response) => response.hasOwnProperty('switch_status'),
          options: { intervalSecs: 5, maxAttempts: 10 },
        })
        if (response.switch_status.hasOwnProperty('error_message')) {
          dispatch('setCrossgradeRequestError', {
            errorMessage: response.switch_status.error_message,
          })
        } else if (!response.switch_status.is_switched) {
          dispatch('setCrossgradeRequestError', {})
        } else if (response.switch_status.hasOwnProperty('is_refunded') && !response.switch_status.is_refunded) {
          // If the switch was successful but not the refund, show a custom error message
          dispatch('closePlanCrossgradeModal')
          dispatch('alertDialog/openAlertDialog', {
            ...ALERT_ICON,
            title: __('Refund failed!'),
            body: __(
              'You have successfully switched plans! Unfortunately, your bank is unable to process your refund. We have extended your term end for now.',
            ),
            closeButtonText: __('Okay'),
            afterCloseActions: [{ type: 'reloadPage' }],
          })
        } else if (getters.crossgradeStep === GenericStep.Now) {
          commit('SHOW_PLAN_CROSSGRADE_MODAL', GenericStep.ConfirmNow)
        } else if (getters.crossgradeStep === GenericStep.Later) {
          commit('SHOW_PLAN_CROSSGRADE_MODAL', GenericStep.ConfirmLater)
        }
      } catch (e) {
        dispatch('setCrossgradeRequestError', {})
      } finally {
        commit('SET_IS_PROCESSING_CROSSGRADE', false)
      }
    },
    async switchDifferentCurrencyImmediately({ commit, getters, dispatch }) {
      try {
        commit('SET_IS_PROCESSING_CROSSGRADE', true)
        const body = asciiSafeStringify({
          plan_id: getters.oppositeSchedulePlanId,
        })
        await PadletApi.Billing.switchCurrency({ body })
        commit('SHOW_PLAN_CROSSGRADE_MODAL', GenericStep.ConfirmNow)
      } catch (e) {
        try {
          if (JSON.parse(e.message).error === 'refund_failed') {
            dispatch('closePlanCrossgradeModal')
            dispatch('alertDialog/openAlertDialog', {
              ...ALERT_ICON,
              title: __('Refund failed!'),
              body: __(
                'Sorry, we were unable to refund your previous subscription through your card. We have extended your current subscription.',
              ),
              closeButtonText: __('Okay'),
              afterCloseActions: [{ type: 'reloadPage' }],
            })
          } else {
            dispatch('setCrossgradeRequestError', { errorMessage: JSON.parse(e.message).error })
          }
        } catch {
          dispatch('setCrossgradeRequestError', {})
        }
      } finally {
        commit('SET_IS_PROCESSING_CROSSGRADE', false)
      }
    },
    setCrossgradeRequestError({ commit }, { errorMessage }: { errorMessage?: string }) {
      commit('SET_CROSSGRADE_ERROR', errorMessage || unableToProcessErrorMessage)
    },
    setDowngradeRequestError({ commit }, { errorMessage }: { errorMessage?: string }) {
      commit('SET_DOWNGRADE_ERROR', errorMessage || unableToProcessErrorMessage)
    },
    verifyIdentity({ state }): void {
      navigateTo(
        transformCurrentUrl(
          {},
          { path: '/auth/verify_identity', search: { email: state.user.email ?? state.user.username ?? '' } },
        ),
      )
    },
    startOffboarding({ getters, dispatch }): void {
      getters.isWebPlan || !getters.user.paying
        ? dispatch('navigateToOffboarding')
        : dispatch('showMobileSubscriptionWarning')
    },
    navigateToOffboarding(): void {
      navigateTo(buildUrlFromPath('/delete-account/confirm'))
    },
    showMobileSubscriptionWarning({ getters, dispatch }) {
      const isAndroid = getters.billing.payment_channel === 'android'
      const isIos = getters.billing.payment_channel === 'ios'
      const showMobileWarningDialog = async (body) =>
        await dispatch('confirmationDialog/openConfirmationDialog', {
          iconSrc: stopHandIcon,
          iconAlt: __('Stop hand'),
          title: __('Subscription still active'),
          body,
          confirmButtonText: __('Continue deleting'),
          cancelButtonText: __('Nevermind'),
          afterConfirmActions: [{ type: 'navigateToOffboarding' }],
          forceFullWidthButtons: true,
          buttonScheme: OzConfirmationDialogBoxButtonScheme.PromoteCancellation,
          shouldFadeIn: false,
        })
      const messages = {
        androidGold: __(
          'You are still subscribed to Gold on Android. Downgrade your account before deleting it to stop recurring payments.',
        ),
        iosGold: __(
          'You are still subscribed to Gold on iOS. Downgrade your account before deleting it to stop recurring payments.',
        ),
        androidPlatinum: __(
          'You are still subscribed to Platinum on Android. Downgrade your account before deleting it to stop recurring payments.',
        ),
        iosPlatinum: __(
          'You are still subscribed to Platinum on iOS. Downgrade your account before deleting it to stop recurring payments.',
        ),
      }
      if (getters.billing.tier === 'gold' && isAndroid) {
        showMobileWarningDialog(messages.androidGold)
      } else if (getters.billing.tier === 'gold' && isIos) {
        showMobileWarningDialog(messages.iosGold)
      } else if (getters.billing.tier === 'platinum' && isAndroid) {
        showMobileWarningDialog(messages.androidPlatinum)
      } else if (getters.billing.tier === 'platinum' && isIos) {
        showMobileWarningDialog(messages.iosPlatinum)
      }
    },

    // Rostering Validation
    // Check App License to see if user needs to be logged out.
    async validateGoogleAppLicense() {
      const validationResponse = await PadletApi.GoogleAppLicensingSettings.checkLicense()
      const licenseStatus = (validationResponse?.data as JsonAPIResource<GoogleAppLicensingValidateResponse>).attributes
      if (!licenseStatus.invalidLicense) return
      navigateTo(licenseStatus.nextUrl as string, { method: 'post' })
    },

    // Communication setting
    async fetchUserWalls({ getters, commit }): Promise<void> {
      try {
        const response = await PadletApi.User.fetchUserWalls(getters.userId)
        // Push notifications automatically by default
        const wallNotifications = response.reduce((acc, wall) => ({ ...acc, [wall.id]: true }), {})

        commit('SET_USER_WALLS', response)
        commit('SET_WALL_NOTIFICATIONS', wallNotifications)
      } catch (e) {
        captureFetchException(e, { source: 'DashboardSettingsFetchUserWalls' })
      }
    },
    async fetchCommunicationSettings({ getters, commit }): Promise<void> {
      try {
        const response = await PadletApi.User.fetchCommunicationSettings(getters.userId)

        commit('SET_COMMUNICATION_SETTINGS', {
          wallCreate: response.communication.wall_create,
          productUpdate: response.communication.product_update,
        })
      } catch (e) {
        captureFetchException(e, { source: 'DashboardSettingsFetchCommunicationSettings' })
      }
    },
    async setCommunicationSettings({ state, getters, commit }, { setting, value }): Promise<void> {
      try {
        commit('SET_COMMUNICATION_SETTINGS', {
          ...state.communicationSettings,
          [setting]: value,
        })
        await PadletApi.User.updateCommunicationSettings(getters.userId, {
          body: asciiSafeStringify({
            communication: {
              wall_create: state.communicationSettings.wallCreate,
              product_update: state.communicationSettings.productUpdate,
            },
          }),
        })
      } catch (e) {
        captureFetchException(e, { source: 'DashboardSettingsSetCommunicationSettings' })
      }
    },
    async toggleAllNotifications({ state, getters, commit }): Promise<void> {
      try {
        const response = await PadletApi.User.toggleAllNotifications({
          body: asciiSafeStringify({ notify_all: getters.xUnsubscribeAllText ? 'false' : 'true' }),
        })
        const wallNotifications = Object.keys(state.wallNotifications).reduce(
          (acc, wallId) => ({ ...acc, [wallId]: response.notify_all }),
          {},
        )

        commit('SET_WALL_NOTIFICATIONS', wallNotifications)
      } catch (e) {
        captureFetchException(e, { source: 'DashboardSettingsToggleAllNotifications' })
      }
    },

    // Tenant info page
    updateCurrentTenant({ commit }, payload: Partial<Tenant>) {
      commit('UPDATE_CURRENT_TENANT', payload)
    },

    // Tenant permissions page
    async fetchTenantPermissions({ state, commit }) {
      const tenantId = state.tenant.id
      try {
        const response = await PadletApi.TenantPermissions.fetch(tenantId)
        const tenant = (response.data as JsonAPIResource<Tenant>).attributes
        commit('UPDATE_CURRENT_TENANT', tenant)
      } catch (e) {
        captureFetchException(e, { source: 'DashboardSettingsFetchTenantPermissions' })
      }
    },
    async toggleTenantPermission({ state, commit, dispatch }, key) {
      const tenant = state.tenant
      const newValue = !tenant?.settings?.permissions?.[key]
      const updatedTenant: Partial<Tenant> = { id: tenant.id, settings: { permissions: { [key]: newValue } } }
      try {
        await PadletApi.TenantPermissions.update(tenant.id, updatedTenant)
        commit('UPDATE_CURRENT_TENANT', updatedTenant)
      } catch (e) {
        captureFetchException(e as FetchError)
        dispatch('globalSnackbar/genericFetchError')
      }
    },

    // Library billing
    async initializeLibraryBillingSettings({ dispatch, getters }): Promise<void> {
      await Promise.allSettled([
        useLibraryPlansStore().fetchPlans({ libraryId: getters.libraryId }),
        dispatch('fetchLibraryBilling'),
        dispatch('fetchLibraryInvoices'),
      ])
    },
    async initializeLibraryDeleteSettings({ dispatch, getters }): Promise<void> {
      await useLibraryPlansStore().fetchPlans({ libraryId: getters.libraryId })
      await dispatch('fetchLibraryBilling')
    },
    async fetchLibraryBilling({ commit, getters }): Promise<void> {
      try {
        commit('SET_IS_LOADING_LIBRARY_BILLING_INFORMATION', true)
        const response = await PadletApi.LibraryBilling.fetch({ libraryId: getters.libraryId })
        commit('FETCHED_LIBRARY_BILLING', response.data)
      } catch {
        commit('SET_LIBRARY_BILLING_ERROR', unableToProcessErrorMessage)
      } finally {
        commit('SET_IS_LOADING_LIBRARY_BILLING_INFORMATION', false)
      }
    },
    async fetchLibraryInvoices({ commit, getters }): Promise<void> {
      try {
        commit('SET_IS_LOADING_LIBRARY_INVOICES', true)
        const response: JsonAPIResponse<LibraryInvoice> = await PadletApi.LibraryInvoice.fetch({
          libraryId: getters.libraryId,
        })
        const libraryInvoices = (response?.data as Array<JsonAPIResource<LibraryInvoice>>)?.map(
          (invoice) => invoice.attributes,
        )
        commit('FETCHED_LIBRARY_INVOICES', libraryInvoices)
      } catch {
        commit('SET_LIBRARY_INVOICES_ERROR', unableToProcessErrorMessage)
      } finally {
        commit('SET_IS_LOADING_LIBRARY_INVOICES', false)
      }
    },
    async undoLibraryScheduledChange({ getters, commit, dispatch }): Promise<void> {
      dispatch('spinnerModal/openSpinnerModal')
      try {
        const scheduledChange: LibraryScheduledChange = getters.currentLibraryScheduledChange
        let response: JsonAPIResponse<UndoLibraryScheduledChangeResult> =
          {} as JsonAPIResponse<UndoLibraryScheduledChangeResult>

        if (scheduledChange === LibraryScheduledChange.DowngradeToTeamNeon) {
          response = await PadletApi.LibraryBilling.undoScheduledDowngradeToTeamNeon({
            libraryId: getters.libraryId,
          })
        } else if (
          scheduledChange === LibraryScheduledChange.SwitchToAnnual ||
          scheduledChange === LibraryScheduledChange.SwitchToMonthly
        ) {
          response = await PadletApi.LibraryBilling.undoScheduledCrossgrade({
            libraryId: getters.libraryId,
          })
        }
        const undoLibraryScheduledChangeResult = (response?.data as JsonAPIResource<UndoLibraryScheduledChangeResult>)
          ?.attributes

        if (!undoLibraryScheduledChangeResult?.success || undoLibraryScheduledChangeResult?.errors?.length) {
          dispatch('showFailedUndoScheduledChangeDialog', { afterConfirmActionType: 'undoLibraryScheduledChange' })
          return
        }
        const successUndoMutations = {
          [LibraryScheduledChange.DowngradeToTeamNeon]: 'UNDO_LIBRARY_SCHEDULED_DOWNGRADE',
          [LibraryScheduledChange.SwitchToMonthly]: 'UNDO_LIBRARY_SCHEDULED_SWITCH',
          [LibraryScheduledChange.SwitchToAnnual]: 'UNDO_LIBRARY_SCHEDULED_SWITCH',
        }
        commit(successUndoMutations[scheduledChange])
      } catch {
        dispatch('showFailedUndoScheduledChangeDialog', { afterConfirmActionType: 'undoLibraryScheduledChange' })
      } finally {
        dispatch('spinnerModal/closeSpinnerModal')
      }
    },
    showUndoLibraryScheduledChangeModal({ getters, dispatch }): void {
      const showConfirmationDialog = async ({ title, body }) =>
        await dispatch('confirmationDialog/openConfirmationDialog', {
          ...THINKING_FACE_ICON,
          title,
          body,
          confirmButtonText: __('Confirm'),
          cancelButtonText: __('Nevermind'),
          afterConfirmActions: [{ type: 'undoLibraryScheduledChange' }],
          forceFullWidthButtons: true,
        })
      if (getters.currentLibraryScheduledChange === LibraryScheduledChange.DowngradeToTeamNeon) {
        if (getters.isLibraryTeamGoldTier as boolean) {
          void showConfirmationDialog({
            title: __('Undo cancellation?'),
            body: __('You will continue to pay for your Team Gold subscription.'),
          })
        }
        if (getters.isLibraryClassroomGoldTier as boolean) {
          void showConfirmationDialog({
            title: __('Undo cancellation?'),
            body: __('You will continue to pay for your classroom subscription.'),
          })
        }
      } else if (getters.currentLibraryScheduledChange === LibraryScheduledChange.SwitchToMonthly) {
        showConfirmationDialog({
          title: __('Cancel change to monthly payments?'),
          body: __('You will continue to pay once per year.'),
        })
      } else if (getters.currentLibraryScheduledChange === LibraryScheduledChange.SwitchToAnnual) {
        showConfirmationDialog({
          title: __('Cancel change to annual payments?'),
          body: __('You will continue to pay once per month.'),
        })
      }
    },
    showLibraryDowngradeToTeamNeonModal({ commit }): void {
      commit('SHOW_LIBRARY_DOWNGRADE_MODAL', LibraryGenericStep.ChooseDate)
    },
    showClassroomLibraryDowngradeModal({ commit }): void {
      commit('SHOW_CLASSROOM_LIBRARY_DOWNGRADE_MODAL', LibraryGenericStep.Later)
    },
    async fetchEstimatedDowngradeTeamNeonCost({ getters, commit, dispatch }): Promise<void> {
      commit('SET_IS_LOADING_TEAM_NEON_DOWNGRADE_INFORMATION', true)
      try {
        const response: JsonAPIResponse<EstimateTeamNeonDowngradeRefund> =
          await PadletApi.LibraryBilling.estimateTeamNeonDowngradeRefund({
            libraryId: getters.libraryId,
          })
        const estimatedTeamNeonDowngradeRefund = (response?.data as JsonAPIResource<EstimateTeamNeonDowngradeRefund>)
          ?.attributes
        commit('SET_ESTIMATED_TEAM_NEON_DOWNGRADE_COST', estimatedTeamNeonDowngradeRefund)
      } catch (e) {
        e.status === 422
          ? dispatch('setLibraryDowngradeError', {
              errorMessage: unpaidInvoiceErrorMessage,
            })
          : dispatch('setLibraryDowngradeError', {})
      } finally {
        commit('SET_IS_LOADING_TEAM_NEON_DOWNGRADE_INFORMATION', false)
      }
    },
    closeLibraryDowngradeModal({ commit }): void {
      commit('CLOSE_LIBRARY_DOWNGRADE_MODAL')
    },
    closeClassroomLibraryDowngradeModal({ commit }): void {
      commit('CLOSE_CLASSROOM_LIBRARY_DOWNGRADE_MODAL')
    },
    changeStepLibraryDowngradeModal(
      { commit, dispatch },
      { libraryDowngradeStep }: { libraryDowngradeStep: LibraryDowngradeStep },
    ): void {
      if (libraryDowngradeStep === LibraryGenericStep.Now) dispatch('fetchEstimatedDowngradeTeamNeonCost')
      commit('CHANGE_STEP_LIBRARY_DOWNGRADE_MODAL', libraryDowngradeStep)
    },
    async downgradeLibraryToTeamNeon(
      { commit, dispatch, getters },
      { cancellationType }: { cancellationType: CancellationType },
    ): Promise<void> {
      try {
        commit('SET_IS_PROCESSING_LIBRARY_DOWNGRADE', true)
        const body = asciiSafeStringify({ cancellation_type: cancellationType })
        const response: JsonAPIResponse<LibraryDowngradeToTeamNeonResult> =
          await PadletApi.LibraryBilling.cancelSubscription({ libraryId: getters.libraryId }, { body })
        const statusLink = (response?.data as JsonAPIResource<LibraryDowngradeToTeamNeonResult>)?.attributes.status_link
        const { data } = await poll({
          pollingUrl: statusLink,
          validationCallback: ({ data }) => data.attributes?.status && data.attributes?.status !== 'active',
          options: { intervalSecs: 5, maxAttempts: 10, method: HTTPMethod.get },
        })
        // Check if refund processed
        if (getters.libraryDowngradeStep === LibraryGenericStep.Now) {
          // TODO: remove "hasOwnProperty" check
          if (data.hasOwnProperty('refunded') && !data.refunded) {
            dispatch('closeLibraryDowngradeModal')
            dispatch('alertDialog/openAlertDialog', {
              ...ALERT_ICON,
              title: __('Refund failed!'),
              body: __(
                'We were unable to process your refund. Our support team will be in contact within 24 hours. Your refund amount will not be affected.',
              ),
              closeButtonText: __('Okay'),
              afterCloseActions: [{ type: 'reloadPage' }],
            })
          } else {
            commit('CHANGE_STEP_LIBRARY_DOWNGRADE_MODAL', LibraryGenericStep.ConfirmNow)
          }
        } else if (getters.libraryDowngradeStep === LibraryGenericStep.Later) {
          commit('CHANGE_STEP_LIBRARY_DOWNGRADE_MODAL', LibraryGenericStep.ConfirmLater)
        }
      } catch (e) {
        dispatch('setLibraryDowngradeError', {})
      } finally {
        commit('SET_IS_PROCESSING_LIBRARY_DOWNGRADE', false)
      }
    },
    downgradeLibraryToTeamNeonImmediately({ dispatch }): void {
      dispatch('downgradeLibraryToTeamNeon', { cancellationType: CancellationType.EndSubscription })
    },
    scheduleLibraryDowngradeToTeamNeon({ dispatch }): void {
      dispatch('downgradeLibraryToTeamNeon', { cancellationType: CancellationType.SetAsNonRenewing })
    },
    setLibraryDowngradeError({ commit }, { errorMessage }: { errorMessage?: string }) {
      commit('SET_LIBRARY_DOWNGRADE_ERROR', errorMessage || unableToProcessErrorMessage)
    },
    showLibraryDowngradeToTeamNeonModalCheckingQuota(
      { dispatch, getters },
      { tierQuota, wallsUsed }: { tierQuota: number; wallsUsed: number },
    ) {
      if (wallsUsed > tierQuota) {
        void dispatch('showCancelSubscriptionConfirmationDialog')
        return
      }

      if (getters.isClassroomLibrary as boolean) {
        void dispatch('showClassroomLibraryDowngradeModal')
      } else {
        void dispatch('showLibraryDowngradeToTeamNeonModal')
      }
    },
    showAboveTeamNeonQuotaConfirmationDialog(
      { dispatch },
      { wallsUsed, tierQuota }: { wallsUsed: number; tierQuota: number },
    ): void {
      void dispatch('showAboveQuotaConfirmationDialog', {
        title: __('Your team is above this tier’s padlet limit'),
        body: __(
          'Your team currently has %{amount} padlets, but our Team Neon tier only supports %{teamNeonQuota}. You won’t lose anything by downgrading but creating and editing padlets may be limited until you remove existing ones.',
          {
            amount: wallsUsed,
            teamNeonQuota: tierQuota,
          },
        ),
        afterConfirmActionType: 'showLibraryDowngradeToTeamNeonModal',
      })
    },
    showCancelSubscriptionConfirmationDialog({ dispatch, rootGetters, getters }): void {
      void dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          ...THINKING_FACE_ICON,
          layoutVariant: useWindowSizeStore().isPhone
            ? ConfirmationDialogLayoutVariant.Drawer
            : ConfirmationDialogLayoutVariant.Large,
          title: __('Cancel subscription?'),
          body: __(
            'You will still have access to your existing padlets but you will no longer be able to make new ones.',
          ),
          confirmButtonText: __('Continue'),
          cancelButtonText: __('Nevermind'),
          afterConfirmActions: [
            {
              type: (getters.isClassroomLibrary as boolean)
                ? 'showClassroomLibraryDowngradeModal'
                : 'showLibraryDowngradeToTeamNeonModal',
            },
          ],
        },
        { root: true },
      )
    },

    // Delete library
    deleteLibrary({ dispatch, getters }): void {
      const title = {
        [LibraryType.Classroom]: __('Delete %{librarySlug} classroom?', { librarySlug: getters.librarySlug }),
        [LibraryType.School]: __('Delete %{librarySlug} school?', { librarySlug: getters.librarySlug }),
        [LibraryType.Team]: __('Delete %{librarySlug} team?', { librarySlug: getters.librarySlug }),
      }[getters.currentLibrary?.libraryType ?? LibraryType.Team]

      void dispatch('confirmationDialog/openConfirmationDialog', {
        title,
        body: __('This action cannot be undone.'),
        confirmButtonText: __('Delete'),
        cancelButtonText: __('Cancel'),
        isCodeProtected: true,
        buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
        afterConfirmActions: [{ type: 'navigateToLibraryOffboarding' }],
        shouldFadeIn: false,
      })
    },
    navigateToLibraryOffboarding({ getters }): void {
      if (getters.currentLibrary?.libraryType === undefined) return
      navigateTo(
        transformCurrentUrl(
          {},
          {
            path: `/${getters.currentLibrary?.libraryType as LibraryType}/delete`,
            search: { library_id: getters.libraryId },
          },
        ),
      )
    },
    changeConnectivity({ commit }, isOnline: boolean): void {
      commit('CHANGE_CONNECTIVITY', isOnline)
    },
    async loadAndRedirectToLibraryChangeCardUrl({ commit, getters }): Promise<void> {
      commit('SET_IS_LOADING_LIBRARY_CHANGE_CARD_URL', true)
      try {
        const response: JsonAPIResponse<ChangeCardUrl> = await PadletApi.LibraryBilling.libraryChangeCardUrl({
          libraryId: getters.libraryId,
        })
        const changeCardUrl = (response?.data as JsonAPIResource<ChangeCardUrl>)?.attributes.change_card_url
        navigateTo(changeCardUrl)
      } catch (e) {
        alert('Something went wrong :(')
      } finally {
        commit('SET_IS_LOADING_LIBRARY_CHANGE_CARD_URL', false)
      }
    },
    async fetchViewableLibraries({ commit, dispatch, getters }): Promise<void> {
      try {
        const response = await PadletApi.Library.fetchViewableLibraries({
          userId: getters.userId,
        })
        const libraries = (response?.data as Array<JsonAPIResource<Library>>)?.map((library) => library.attributes)
        commit('SET_LIBRARIES', libraries)
        useSettingsNavigationStore().libraries = libraries
      } catch (e) {
        captureFetchException(e, { source: 'DashboardSettingsFetchViewableLibraries' })
        dispatch('globalSnackbar/genericFetchError')
      }
    },
    updateUserOptions({ commit }, options: any): void {
      commit('UPDATE_USER_OPTIONS', options)
    },
  },
}

export { GenericStep, LibraryGenericStep, LibraryScheduledChange, LibrarySubscriptionStatus, ScheduledChange }
export type { CrossgradeStep, DashboardSettingsState, DowngradeStep, LibraryDowngradeStep }
